import React, { useMemo } from 'react';
import { Box, Card, Center, Group, Stack, Text, ThemeIcon, Title } from '@mantine/core';
import { IconChartBar, IconUser, IconUsers, IconUsersGroup } from '@tabler/icons-react';
import { timeAgo } from '../../utils/timeAgo';

// Derived from real timestamps already loaded for the dashboard (personas,
// friends, groups) instead of the old hardcoded single "Created persona X /
// 2 hours ago" entry. No activity-log table exists yet (see
// database/SCHEMA_NOTES.md-adjacent discussion) so this is assembled
// client-side rather than fabricated.
const ActivityFeed = ({ personas, friends, groups }) => {
  const items = useMemo(() => {
    const events = [];

    personas.forEach(p => {
      if (p.created_at) {
        events.push({
          id: `persona-${p.id}`,
          date: p.created_at,
          icon: IconUser,
          text: `You created a persona for ${p.name}`
        });
      }
    });

    friends.forEach(f => {
      if (f.friendship_date) {
        events.push({
          id: `friend-${f.friendship_id || f.id}`,
          date: f.friendship_date,
          icon: IconUsers,
          text: `You and ${f.name} became friends`
        });
      }
    });

    groups.forEach(g => {
      if (g.created_at) {
        events.push({
          id: `group-${g.id}`,
          date: g.created_at,
          icon: IconUsersGroup,
          text: g.role === 'owner' ? `You created ${g.name}` : `You joined ${g.name}`
        });
      }
    });

    return events
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, 6);
  }, [personas, friends, groups]);

  return (
    <Box>
      <Title order={3} size="h5" mb="sm">Recent activity</Title>
      {items.length === 0 ? (
        <Card p="lg" radius="lg" withBorder>
          <Center py="sm">
            <Stack align="center" gap={4}>
              <ThemeIcon size={40} radius="xl" variant="light" color="gray">
                <IconChartBar size={20} />
              </ThemeIcon>
              <Text c="dimmed" size="sm" ta="center">Nothing here yet</Text>
            </Stack>
          </Center>
        </Card>
      ) : (
        <Stack gap={6}>
          {items.map(item => (
            <Card key={item.id} p="sm" radius="md" withBorder>
              <Group gap="sm" wrap="nowrap">
                <ThemeIcon size="md" radius="xl" variant="light" color="gray">
                  <item.icon size={15} />
                </ThemeIcon>
                <Box style={{ flex: 1, minWidth: 0 }}>
                  <Text size="sm" truncate>{item.text}</Text>
                  <Text size="xs" c="dimmed">{timeAgo(item.date)}</Text>
                </Box>
              </Group>
            </Card>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default ActivityFeed;
